import { getPdfMake, getLogoDataUri } from './pdfLoader';
import { CLINIC_IDENTITY } from './clinicIdentity';
import { supabase } from '../supabase/client';
import {
  buildAsistenciaCertDocDefinition,
  buildTratamientoLaboralCertDocDefinition,
  buildLegalJudicialCertDocDefinition,
  buildTextoLibreCertDocDefinition,
} from './certificateBuilders';
import type { CertificateType, EmitCertificadoResult } from './types';

export type { CertificateType } from './types';

export interface EmitirCertificadoParams {
  patientId: string;
  therapistId: string;
  tipoCertificado: CertificateType;
  fechaDesde?: string;
  fechaHasta?: string;
  destinatario?: string;
  textoLibre?: string;
  // Ver comentario en ExportSessionParams.win (src/lib/clinicalExport/index.ts)
  // — misma razón: RPC + carga de pdfmake son async, así que la ventana debe
  // abrirse sincrónica en el click handler antes de llamar acá.
  win: Window | null;
}

const BUILDERS: Record<CertificateType, (result: EmitCertificadoResult, logoDataUri: string | null) => any> = {
  asistencia: buildAsistenciaCertDocDefinition,
  tratamiento_laboral: buildTratamientoLaboralCertDocDefinition,
  legal_judicial: buildLegalJudicialCertDocDefinition,
  texto_libre: buildTextoLibreCertDocDefinition,
};

// Emite un certificado (RPC emitir_certificado, SECURITY DEFINER) y dispara
// la descarga del PDF correspondiente. La RPC valida ownership del paciente
// server-side (con el mismo fallback de pacientes huérfanos que
// update_session_clinical_notes) y persiste un snapshot auditable en
// certificados_emitidos — ver
// sql/migrations/ficha_clinica_v3_certificados_2026_08_20.sql sección 5.
export async function emitirCertificado(params: EmitirCertificadoParams): Promise<void> {
  const { patientId, therapistId, tipoCertificado, fechaDesde, fechaHasta, destinatario, textoLibre, win } = params;

  const { data, error } = await supabase.rpc('emitir_certificado', {
    p_patient_id: patientId,
    p_therapist_id: therapistId,
    p_tipo_certificado: tipoCertificado,
    p_fecha_desde: fechaDesde ?? null,
    p_fecha_hasta: fechaHasta ?? null,
    p_destinatario: destinatario ?? null,
    p_texto_libre: textoLibre ?? null,
  });

  // Nunca exponer el error crudo de Postgres al usuario — mismo criterio que
  // el resto del proyecto sobre errores de Supabase (mensajes genéricos).
  if (error || !data) {
    throw new Error('No se pudo generar el certificado');
  }

  const result = data as EmitCertificadoResult;

  const [pdfMake, logoDataUri] = await Promise.all([getPdfMake(), getLogoDataUri(CLINIC_IDENTITY.logoPath)]);

  const docDefinition = BUILDERS[result.tipo_certificado](result, logoDataUri);

  await pdfMake.createPdf(docDefinition).open(win ?? undefined);
}
